import { AxiosError } from "axios";
import type { RouteLocation, RouteLocationNormalized } from "vue-router";

import Auth from "@/backend/http/auth";
import { loggedIn } from "@/backend/read/BackendReader";
import type { UserResponse } from "@/backend/serverModel";
import { CancelError } from "@/error/CancelError";
import { addBreadcrumb, captureException, captureMessage } from "@/error/sentry";
import { technicalUser } from "@/model/user";
import { useCompanyStore } from "@/store/company";
import { useLoadingStore } from "@/store/loading";
import { isBrowserTabActive } from "@/utils/dom/dom";
import { checkShouldAutoLogout, cleanupInactivityStorage } from "@/utils/inactivityLogout";

import { login } from "../login";
import { goToLogout } from "../navigation";
import { companyParam } from "./utils";

export async function assertLoggedIn(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  // Check if authentication is needed for route
  if (to.matched.some((route) => route.meta.needsAuth === false)) {
    // Attempt authentication if possible, but don't require it
    if (!loggedIn() && to.meta.optionalAuth) {
      try {
        const response = await useLoadingStore().load("checkAuth", Auth.isAuthenticated());
        if (response.success) {
          await loginWithUser(response, to);
        }
      } catch {
        // Auth unavailable — continue to route without session
      }
    }
    return;
  }

  // If logged in, check if onboarding is needed
  if (loggedIn()) {
    // Don't redirect to onboard if already on the onboard page
    if (to.name !== "onboard" && useCompanyStore().shouldOnboard) {
      return { name: "onboard" };
    }
    return;
  }

  useLoadingStore().start();
  return await loginAndRedirect(to, from);
}

async function loginAndRedirect(route: RouteLocation, from: RouteLocation) {
  // Check if user should be logged out due to inactivity
  if (checkShouldAutoLogout()) {
    cleanupInactivityStorage();
    void goToLogout("inactivity");
    return false;
  }

  if (!loggedIn()) {
    addBreadcrumb("page-load", { data: routeInfo(route) });
  }
  // In case we specify ?company=xxx as a query parameter, we should redirect the user directly to the login page.
  // This is used in case of SSO to direct the user directly to the login page where the can click on the SSO-button.
  // When the user never authenticated before, we don't recognize the e-mail thus need to
  // know the company (which in this case is passed as param).
  if (companyParam()) {
    return { name: "login" };
  }
  try {
    const response = await useLoadingStore().load("checkAuth", Auth.isAuthenticated());
    if (response.success) {
      return await loginWithUser(response, route);
    }
    captureMessage("Login unsuccessful with non-error status code", {
      response: { ...response },
      from: routeInfo(from),
      to: routeInfo(route),
    });
    return { name: "login" };
  } catch (e) {
    if (e instanceof CancelError) {
      return false;
    }
    const is401 = e instanceof AxiosError && e.response?.status === 401;
    const isTokenExpired = is401 && e.response?.data?.error?.code === "TOKEN_EXPIRED";

    // isAuthenticated() uses rawClient (no auth interceptor), so 401s throw directly.
    // TOKEN_EXPIRED: attempt a silent token refresh before giving up.
    if (isTokenExpired) {
      try {
        const response = await Auth.refreshToken({});
        return await loginWithUser(response, route);
      } catch {
        return { name: "login" };
      }
    }

    // Any other 401 - redirect without reporting to Sentry.
    if (is401) {
      return { name: "login" };
    }
    // a page reload can be triggered even for a non-active browser tab
    // e.g. when the access token times out
    // in this case, just go to login without a sentry message
    if (isBrowserTabActive()) {
      captureException(e, { from: routeInfo(from), to: routeInfo(route) });
    }
    return { name: "login" };
  }
}

function loginWithUser(response: UserResponse, route: RouteLocation) {
  const user = technicalUser(response.user_id, response.company, response.role);
  return useLoadingStore().load("login", login(user, { ...route.query, path: route.path }));
}

function routeInfo(route: RouteLocation) {
  return {
    originalPath: route.redirectedFrom?.fullPath,
    fullPath: route.fullPath,
    params: route.params,
    query: route.query,
  };
}
